/**
 * @fileoverview PriceRangesView — Cross-vendor cross-org normalized-price
 * aggregation page (SuperAdmin → Intelligence → Price ranges).
 *
 * Phase 11 Wave 8 (BROWSER-01 + NORM-02). Mounts the AdminDataBrowser shell
 * bound to `api.adminDataList(adminApiPrefix, 'price-ranges', params)` with
 * a 7-column config: Item / Unit / Min $ / Avg $ / Max $ / Vendors /
 * Cheapest vendor. The cheapest-vendor cell renders a button that, when
 * clicked, invokes `onDrillToVendor(vendor_id)` so the parent SuperAdmin
 * page can switch to the Data → Line items tab focused on that vendor.
 *
 * Items where neither line_items nor sku_catalog has curated `base_unit`
 * + `base_units_per_pack` don't appear here — the backend CTE filters
 * them out because they cannot be normalized (the Setup → SKU catalog
 * page is where operators curate those columns).
 */

import React from 'react';
import PropTypes from 'prop-types';
import AdminDataBrowser from './AdminDataBrowser';
import api from '../services/api';

/**
 * Format a normalized price value as 4-decimal mono-font money.
 *
 * @param {number|null|undefined} value
 * @returns {string} `$0.6944` style — or `—` when value is null/undefined.
 */
const formatMoney = (value) =>
  value == null ? '—' : `$${Number(value).toFixed(2)}`;

/**
 * PriceRangesView — Cross-vendor cross-org normalized-price aggregation.
 *
 * @param {Object} props
 * @param {string} props.adminApiPrefix - Full admin API prefix
 *   (e.g. `/api/{secret}/admin`). Passed straight to api.adminDataList.
 * @param {function} [props.onDrillToVendor] - Optional callback invoked
 *   with `(vendor_id: number)` when the operator clicks a row's cheapest
 *   vendor button. Parent SuperAdmin page typically uses this to switch
 *   activeTab to 'data-line-items' filtered by vendor.
 * @returns {JSX.Element}
 */
function PriceRangesView({ adminApiPrefix, onDrillToVendor }) {
  const columns = [
    { key: 'item_canonical_name', label: 'Item' },
    { key: 'canonical_unit',      label: 'Unit' },
    {
      key: 'min_price',
      label: 'Min $',
      mono: true,
      format: (v) => formatMoney(v),
    },
    {
      key: 'avg_price',
      label: 'Avg $',
      mono: true,
      format: (v) => formatMoney(v),
    },
    {
      key: 'max_price',
      label: 'Max $',
      mono: true,
      format: (v) => formatMoney(v),
    },
    {
      key: 'vendor_count',
      label: 'Vendors',
      mono: true,
      format: (v) => (v == null ? '—' : Number(v).toString()),
    },
    {
      key: 'cheapest_vendor_name',
      label: 'Cheapest vendor',
      format: (v, row) => {
        if (!v) return '—';
        return (
          <button
            type="button"
            className="btn btn-ghost btn-xs price-ranges__drill-btn"
            onClick={(e) => {
              // Stop the row click from also firing the AdminDataBrowser
              // detail modal; the operator's intent here is "drill into
              // this vendor", not "see the raw row JSON".
              e.stopPropagation();
              if (onDrillToVendor && row.cheapest_vendor_id != null) {
                onDrillToVendor(row.cheapest_vendor_id);
              }
            }}
            aria-label={`Drill into ${v} (cheapest at ${formatMoney(row.cheapest_normalized_price)})`}
          >
            {v} @ {formatMoney(row.cheapest_normalized_price)}
          </button>
        );
      },
    },
  ];

  return (
    <div className="price-ranges-view">
      <div className="page-header">
        <h2>Price ranges (system-wide)</h2>
        <p className="hint">
          Cheapest vendor per item across all organizations. Items without
          a curated base_unit + base_units_per_pack on Setup → SKU catalog
          don't appear here — they cannot be normalized.
        </p>
      </div>
      <AdminDataBrowser
        endpoint={(params) => api.adminDataList(adminApiPrefix, 'price-ranges', params)}
        onExport={(params) => api.adminDataExport(adminApiPrefix, 'price-ranges', params)}
        columns={columns}
        searchPlaceholder="Search items..."
        emptyTitle="No normalized data yet"
        emptyDescription="Curate base_unit + base_units_per_pack on SKUs in Setup → SKU catalog to populate this view."
      />
    </div>
  );
}

PriceRangesView.propTypes = {
  /** Full admin API prefix (e.g. `/api/{secret}/admin`). */
  adminApiPrefix: PropTypes.string.isRequired,
  /** Optional callback: `(vendor_id: number) => void`. */
  onDrillToVendor: PropTypes.func,
};

PriceRangesView.defaultProps = {
  onDrillToVendor: undefined,
};

export default PriceRangesView;
